import * as React from 'react';
import { useObjectRef } from 'react-aria';
import {
    type Control,
    type FieldValues,
    type Path,
    type UseControllerProps,
    useController,
} from 'react-hook-form';

import type * as api from '@gitbook/api-client';
import type { ContentRefKind } from '@gitbook/app-content-refs';
import { resolveNavigationScreenToHref } from '@gitbook/app-navigation';
import { useHiveClient } from '@gitbook/hive-react';
import { typeQueryInPalette, usePaletteHandle } from '@gitbook/react-uikit';
import type { Document } from '@gitbook/slate';
import { renderIcon, TextInput, type TextInputProps } from '@gitbook/spine-react';
import { useStateWithDeps } from '@gitbook/util-react-hooks';
import { normalizeUrlLikeStringToUrl } from '@gitbook/util-url';

import { useContentReferenceContext } from './context';
import { createInputContentRefPalette } from './createInputContentRefPalette';
import type { UIResolvedContentReference } from './types';
import { useContentReference } from './useContentReference';

/**
 * Input to edit a content reference.
 */
export type ContentReferenceInputProps = {
    value: api.ContentRef | null | undefined;
    onChange: (ref: api.ContentRef | null) => void;
    /**
     * List of accepted references.
     */
    accept?: ContentRefKind[];
    clearable?: boolean;
    document?: Document;
} & Pick<
    TextInputProps,
    | 'size'
    | 'onBlur'
    | 'placeholder'
    | 'autoFocus'
    | 'disabled'
    | 'name'
    | 'feedback'
    | 'aria-invalid'
    | 'trailing'
>;

const DEFAULT_SIZE = 'large';

export type ContentReferenceInputFieldProps<
    TFieldValues extends FieldValues = FieldValues,
    TContext = any,
    TTransformedValues = TFieldValues,
> = {
    name: Path<TFieldValues>;
    control: Control<TFieldValues, TContext, TTransformedValues>;
    rules?: UseControllerProps<TFieldValues, Path<TFieldValues>>['rules'];
    required?: boolean;
} & Omit<
    ContentReferenceInputProps,
    'value' | 'onChange' | 'onBlur' | 'name' | 'feedback' | 'aria-invalid'
>;

export function ContentReferenceInputField<
    TFieldValues extends FieldValues = FieldValues,
    TContext = any,
    TTransformedValues = TFieldValues,
>(props: ContentReferenceInputFieldProps<TFieldValues, TContext, TTransformedValues>) {
    const { name, control, rules, required, ...rest } = props;
    const { field, fieldState } = useController({
        name,
        control,
        rules: rules ?? (required ? { required: 'Required ' } : undefined),
    });

    return (
        <ContentReferenceInput
            {...field}
            {...rest}
            disabled={rest.disabled || field.disabled}
            feedback={
                fieldState.isTouched && fieldState.error?.message
                    ? { type: 'danger', message: fieldState.error.message }
                    : undefined
            }
            aria-invalid={fieldState.invalid || undefined}
        />
    );
}

/**
 * Input that allow to pick a content reference.
 */
export function ContentReferenceInput({
    ref,
    ...props
}: ContentReferenceInputProps & {
    ref?: React.ForwardedRef<HTMLInputElement>;
}): React.ReactElement {
    return (
        <React.Suspense
            fallback={<TextInput disabled value="Loading..." size={props.size ?? DEFAULT_SIZE} />}
        >
            <ContentReferenceInputInner ref={ref} {...props} />
        </React.Suspense>
    );
}

/**
 * Inner component that suspends.
 */
const ContentReferenceInputInner = function ContentReferenceInputInner({
    ref,
    ...props
}: ContentReferenceInputProps & {
    ref?: React.ForwardedRef<HTMLInputElement>;
}): React.ReactElement {
    const {
        value,
        onChange,
        accept,
        document: slateDocument,
        size = DEFAULT_SIZE,
        onBlur,
        clearable = true,
        trailing,
        ...rest
    } = props;

    // Resolves the content ref, it suspends
    const resolved = useContentReference(value);

    // References to DOM elements
    const inputRef = useObjectRef(ref);
    const containerRef = React.useRef<HTMLDivElement>(null);

    // The string representation of the value provided.
    const strValue = React.useMemo(
        () => getStringFromContentRef(value, resolved),
        [value, resolved]
    );

    // Input value kept synced with the value provided
    // so when the user selects it, it resets
    const [inputValue, setInputValue] = useStateWithDeps(strValue, [strValue]);

    // Reset the input value to its initial state.
    const resetInputValue = () => setInputValue(strValue);

    // We consider the input dirty if the user has typed
    // something difference from the initial value.
    const isDirty = inputValue !== strValue;

    // Always search with the displayed value so reopening an existing link shows its target.
    const query = inputValue;

    // Called when the user is done editing
    // - The palette is closed and the input is not focused
    const onDone = () => {
        resetInputValue();
        const parsedHref = isDirty ? normalizeUrlLikeStringToUrl(inputValue) : null;
        if (parsedHref) {
            onChange({ kind: 'url', url: parsedHref });
        }
    };

    // Handle to control the palette.
    const paletteHandle = usePaletteHandle(containerRef, {
        onClose: () => {
            // If the input is not focused, reset.
            if (document.activeElement !== inputRef.current) {
                onDone();
            }
        },
        inputRef,
    });

    // Dependencies to create the palette.
    const hiveClient = useHiveClient();
    const contentReferenceContext = useContentReferenceContext();
    const paletteContentReferenceContext = React.useMemo(
        () =>
            slateDocument
                ? { ...contentReferenceContext, document: slateDocument }
                : contentReferenceContext,
        [contentReferenceContext, slateDocument]
    );
    const contentPaletteBase = React.useMemo(
        () =>
            createInputContentRefPalette(onChange, paletteContentReferenceContext, hiveClient, {
                search: { enabled: false },
                accept,
                value,
                constrainHeight: true,
            }),
        [accept, hiveClient, onChange, paletteContentReferenceContext, value]
    );
    const urlPaletteBase = React.useMemo(
        () =>
            createInputContentRefPalette(onChange, paletteContentReferenceContext, hiveClient, {
                search: { enabled: false },
                accept: ['url'],
                value,
                constrainHeight: true,
            }),
        [hiveClient, onChange, paletteContentReferenceContext, value]
    );

    // Set a query into the palette, open it if not opened.
    const createPalette = (query: string) => {
        const isTypingURL = query.startsWith('http') || query.startsWith('www');
        const paletteBase = isTypingURL ? urlPaletteBase : contentPaletteBase;
        return typeQueryInPalette(paletteBase, query);
    };

    const setPaletteQuery = (query: string) => {
        paletteHandle.open(createPalette(query));
    };

    return (
        <div ref={containerRef} className="flex flex-col gap-1">
            <TextInput
                ref={inputRef}
                aria-label="Edit link"
                data-testid="contentRef.input"
                size={size}
                value={inputValue}
                onChange={(event) => {
                    const input = event.target.value;
                    setInputValue(input);
                    setPaletteQuery(input);
                }}
                onFocus={() => {
                    setPaletteQuery(query);
                }}
                onBlur={(event) => {
                    if (!paletteHandle.opened) {
                        onDone();
                    }
                    onBlur?.(event);
                }}
                onKeyDown={(event) => {
                    if (event.key === 'ArrowDown') {
                        event.preventDefault();
                        setPaletteQuery(query);
                    }
                }}
                onClear={
                    clearable && value
                        ? () => {
                              setInputValue('');
                              onChange(null);
                              inputRef.current?.focus();
                          }
                        : undefined
                }
                prefix={
                    value && !isDirty && resolved.icon ? (
                        <span className="flex items-center text-secondary">
                            {renderIcon(resolved.icon, {
                                size: 16,
                                className: 'text-secondary',
                            })}
                        </span>
                    ) : undefined
                }
                trailing={trailing}
                {...rest}
            />
        </div>
    );
};

/**
 * Return the string representation of a content ref to be displayed in the content ref input.
 */
export function getStringFromContentRef(
    contentRef: api.ContentRef | null | undefined,
    resolved: UIResolvedContentReference
) {
    if (!contentRef) {
        return '';
    }

    if (contentRef.kind === 'anchor' || contentRef.kind === 'url') {
        if (resolved.resolution && typeof resolved.resolution.target !== 'string') {
            return resolveNavigationScreenToHref(resolved.resolution.target);
        }

        return resolved.href;
    }

    return resolved.title;
}
